import { createSlice } from '@reduxjs/toolkit';
import { merge } from 'lodash';

import { signOut } from '../../../slices/authSlice';
import { addFolderToTree, dropInFolderAlphabetically, editFolderInTree, removeNodeWithIdFromArray } from '../../../utils/treeHelpers';
import { FolderTree } from '../../types';
import {
  createFolder,
  deleteFolder,
  editFolder,
  fetchFoldersForTeam,
  fetchPrivateFolders,
  moveFolder,
  openFolder,
  selectFolderId
} from './actions';

export interface Breadcrumb {
  id: string;
  label: string;
}

export interface FolderMoveError {
  folderName: string;
  message: string;
}

export interface FoldersState {
  folders: Array<FolderTree>;
  privateFolders: Array<FolderTree>;
  selectedFolderId: string | null;
  foldersForTeamId: string | null;
  loading: boolean;
  breadcrumbs: Array<Breadcrumb>;
  moveError?: FolderMoveError;
}

const calculateBreadcrumbs = (selectedFolderId: string, folders: Array<FolderTree>): Array<Breadcrumb> => {
  if (folders.length === 0) {
    return [];
  }

  for (let i = 0; i < folders.length; i++) {
    const folder = folders[i];

    if (folder.id === selectedFolderId) {
      return [
        {
          id: folder.id,
          label: folder.name
        }
      ];
    }
    if (folder.children) {
      const found = calculateBreadcrumbs(selectedFolderId, folder.children);

      if (found.length) {
        return [
          {
            id: folder.id,
            label: folder.name
          },
          ...found
        ];
      }
    }
  }

  return [];
};

export const initialState: FoldersState = {
  folders: [],
  privateFolders: [],
  selectedFolderId: null,
  foldersForTeamId: null,
  loading: false,
  breadcrumbs: []
};

export const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    dismissMoveError: (state) => {
      delete state.moveError;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(selectFolderId, (state, action) => {
        state.selectedFolderId = action.payload;
        if (state.selectedFolderId) {
          const folderIsPrivate = JSON.stringify(state.privateFolders).includes(action.payload);

          state.breadcrumbs = calculateBreadcrumbs(state.selectedFolderId, folderIsPrivate ? state.privateFolders : state.folders);
        } else {
          state.breadcrumbs = [];
        }
      })
      .addCase(openFolder, (state, action) => {
        const openedFolder = action.payload;
        const folderIsPrivate = openedFolder.organization_permission === null;

        if (folderIsPrivate) {
          state.privateFolders = editFolderInTree(state.privateFolders, openedFolder);
        } else {
          state.folders = editFolderInTree(state.folders, openedFolder);
        }
      })
      .addCase(fetchFoldersForTeam.pending, (state, action) => {
        const { teamId } = action.meta.arg;

        if (teamId != state.foldersForTeamId) {
          state.folders = [];
          state.foldersForTeamId = teamId;
        }
        state.loading = true;
      })
      .addCase(fetchFoldersForTeam.fulfilled, (state, action) => {
        if (state.folders.length) {
          // merge folders to persist the open ones
          state.folders = merge(state.folders, action.payload);
        } else {
          state.folders = action.payload;
        }
        state.loading = false;
      })
      .addCase(fetchPrivateFolders.pending, (state, action) => {
        const { teamId } = action.meta.arg;

        if (teamId != state.foldersForTeamId) {
          state.privateFolders = [];
          state.foldersForTeamId = teamId;
        }
        state.loading = true;
      })
      .addCase(fetchPrivateFolders.fulfilled, (state, action) => {
        if (state.folders.length) {
          // merge folders to persist the open ones
          state.privateFolders = merge(state.privateFolders, action.payload);
        } else {
          state.privateFolders = action.payload;
        }
        state.loading = false;
      })
      .addCase(editFolder.fulfilled, (state, action) => {
        const { response: newFolder, folderIsPrivate } = action.payload;

        if (folderIsPrivate) {
          state.privateFolders = editFolderInTree(state.privateFolders, newFolder);
        } else {
          state.folders = editFolderInTree(state.folders, newFolder);
        }
        if (state.selectedFolderId) {
          state.breadcrumbs = calculateBreadcrumbs(state.selectedFolderId, state.folders);
        } else {
          state.breadcrumbs = [];
        }
        state.loading = false;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        const { id: deletedId, folderIsPrivate } = action.payload;

        if (folderIsPrivate) {
          state.privateFolders = removeNodeWithIdFromArray(state.privateFolders, deletedId);
        } else {
          state.folders = removeNodeWithIdFromArray(state.folders, deletedId);
        }

        if (state.selectedFolderId) {
          state.breadcrumbs = calculateBreadcrumbs(state.selectedFolderId, state.folders);
          if (state.breadcrumbs.length === 0) {
            state.selectedFolderId = null;
          }
        } else {
          state.breadcrumbs = [];
        }
        state.loading = false;
      })
      .addCase(createFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        const isPrivateFolder = action.payload.organization_permission === null;

        if (isPrivateFolder) {
          state.privateFolders = addFolderToTree(state.privateFolders, action.payload, action.payload.parent_id);
        } else {
          state.folders = addFolderToTree(state.folders, action.payload, action.payload.parent_id);
        }
        state.loading = false;
      })
      .addCase(createFolder.rejected, (state) => {
        state.loading = false;
      })
      .addCase(moveFolder.fulfilled, (state, action) => {
        const folderIsPrivate = action.payload;

        if (folderIsPrivate) {
          state.privateFolders = dropInFolderAlphabetically(state.privateFolders, action.meta.arg.folder.id, action.meta.arg.parentId);
        } else {
          state.folders = dropInFolderAlphabetically(state.folders, action.meta.arg.folder.id, action.meta.arg.parentId);
        }
      })
      .addCase(moveFolder.rejected, (state, action) => {
        state.moveError = {
          folderName: action.meta.arg.folder.name,
          message: action.payload[0]
        };
      })
      .addCase(signOut, () => initialState);
  }
});

export const { dismissMoveError } = folderSlice.actions;

export default folderSlice.reducer;
